/**
 * Fase 2B · B2 · NextBestActionHero
 * Hero card contextual del Dashboard del titular · reemplaza los KPIs 0€ desmoralizantes.
 * Estados según /api/titular/activation/status:
 *   1. configure_center      → "Completa los datos de tu centro"
 *   2. add_first_service     → "Añade tu primer servicio"
 *   3. configure_availability→ "Configura tu disponibilidad"
 *   4. get_first_booking     → "Consigue tu primera reserva" (post-activación)
 *   5. manage_bookings       → "Tienes N citas en marcha"
 *
 * También muestra un progress con los 3 pasos de activación y el time-to-value.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Skeleton } from "./ui/skeleton";
import {
  ArrowRight, CheckCircle, Circle, Storefront, Wrench, Clock,
  Sparkle, Rocket, Target, Confetti,
} from "@phosphor-icons/react";

const NBA_ICONS = {
  configure_center: Storefront,
  add_first_service: Wrench,
  configure_availability: Clock,
  get_first_booking: Target,
  manage_bookings: Confetti,
};

function StepBadge({ done, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
        done
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
          : "bg-[var(--ag-midnight)]/60 text-[var(--ag-text-dim)] border border-[var(--ag-border)]"
      }`}
      data-testid={`nba-step-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {done ? <CheckCircle size={11} weight="fill" /> : <Circle size={11} />}
      {label}
    </span>
  );
}

export default function NextBestActionHero({ onLoaded }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/titular/activation/status");
        if (!cancelled) {
          setStatus(data);
          if (typeof onLoaded === "function") onLoaded(data);
        }
      } catch { /* silent · fallback ocultar */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  if (!status) return null;

  const nba = status.next_best_action || {};
  const Icon = NBA_ICONS[nba.key] || Rocket;
  const isActivated = !!status.is_activated;
  const progressPct = status.progress_pct ?? 0;

  // Visual tone by state
  const toneClass = isActivated
    ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/8 to-transparent"
    : "border-[var(--ag-gold)]/40 bg-gradient-to-br from-[var(--ag-gold)]/10 to-transparent";
  const iconTone = isActivated ? "text-emerald-300 bg-emerald-500/15" : "text-[var(--ag-gold)] bg-[var(--ag-gold)]/15";

  const cta = nba.cta_route || "/";
  const isExternal = cta.startsWith("http");

  const CtaButton = ({ children }) => (
    isExternal ? (
      <a
        href={cta}
        target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded font-semibold text-sm ag-btn-gold whitespace-nowrap"
        data-testid="nba-cta"
      >
        {children}
      </a>
    ) : (
      <button
        type="button"
        onClick={() => navigate(cta)}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded font-semibold text-sm ag-btn-gold whitespace-nowrap"
        data-testid="nba-cta"
      >
        {children}
      </button>
    )
  );

  return (
    <section
      className={`ag-card p-5 md:p-6 border ${toneClass}`}
      data-testid="nba-hero"
      data-nba-key={nba.key}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded flex items-center justify-center shrink-0 ${iconTone}`}>
            <Icon size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold flex items-center gap-2 flex-wrap">
              <Sparkle size={11} weight="fill" />
              {isActivated ? "Tu próximo paso" : "Empieza por aquí"}
              {!isActivated && (
                <span className="text-[var(--ag-text-dim)] font-normal tabular-nums">
                  · {status.completed}/{status.total} completados
                </span>
              )}
            </div>
            <h2 className="font-display text-xl md:text-2xl font-black tracking-tighter mt-1" data-testid="nba-title">
              {nba.title}
            </h2>
            <p className="text-sm text-[var(--ag-text-dim)] mt-1.5" data-testid="nba-description">
              {nba.description}
            </p>
          </div>
        </div>
        <div className="shrink-0">
          <CtaButton>
            {nba.cta_label} <ArrowRight size={13} weight="bold" />
          </CtaButton>
        </div>
      </div>

      {/* Progress · solo si no activado */}
      {!isActivated && (
        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap" data-testid="nba-steps">
            {status.steps?.map((s) => (
              <StepBadge
                key={s.id}
                done={s.done}
                label={s.id === "center" ? "Centro" : s.id === "services" ? `Servicios${s.count ? ` (${s.count})` : ""}` : "Horario"}
              />
            ))}
          </div>
          <div className="h-2 w-full bg-[var(--ag-midnight)]/60 border border-[var(--ag-border)] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[var(--ag-gold)] to-[var(--ag-gold-strong)] transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="nba-progress-bar"
            />
          </div>
        </div>
      )}

      {/* Time to value · post activation */}
      {isActivated && status.time_to_value_hours != null && status.time_to_value_hours < 24 * 30 && (
        <div className="mt-4 pt-4 border-t border-[var(--ag-border)]/50 flex items-center justify-between text-[11px] text-[var(--ag-text-dim)]">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle size={12} weight="fill" className="text-emerald-400" />
            Centro activado
          </span>
          <span className="tabular-nums">
            Time-to-value: {status.time_to_value_hours < 1
              ? `${Math.round(status.time_to_value_hours * 60)} min`
              : `${status.time_to_value_hours.toFixed(1)} h`}
          </span>
        </div>
      )}
    </section>
  );
}
